import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'
import api from '../services/api'

interface FilterTemplate {
  id: string
  name: string
  description: string
  icon: string
  category: string
  filters: Record<string, unknown>
  is_template: boolean
}

interface FilterField {
  field: string
  label: string
  type: string
  options?: string[]
  min?: number
  max?: number
}

interface SavedSearch {
  id: string
  name: string
  description?: string
  category: string
  filters: Record<string, unknown>
  is_default: boolean
  created_at: string
  last_used_at?: string
  use_count: number
}

interface FilterCondition {
  field: string
  operator: string
  value: string | number | boolean | string[]
}

const AdvancedFilters: React.FC = () => {
  const [templates, setTemplates] = useState<FilterTemplate[]>([])
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([])
  const [activeCategory, setActiveCategory] = useState<string>('markets')
  const [filterFields, setFilterFields] = useState<FilterField[]>([])
  const [activeFilters, setActiveFilters] = useState<FilterCondition[]>([])
  const [searchResults, setSearchResults] = useState<Record<string, unknown>[]>([])
  const [searchName, setSearchName] = useState('')
  const [searchDescription, setSearchDescription] = useState('')
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const categories = [
    { id: 'markets', name: 'Markets', icon: '🌍' },
    { id: 'sellers', name: 'Sellers', icon: '👥' },
    { id: 'products', name: 'Products', icon: '📦' },
    { id: 'orders', name: 'Orders', icon: '🛒' },
    { id: 'compliance', name: 'Compliance', icon: '✅' },
  ]

  const operators = [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '≠' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'contains', label: 'Contains' },
    { value: 'in', label: 'In' },
  ]

  useEffect(() => {
    loadTemplates()
    loadSavedSearches()
  }, [])

  useEffect(() => {
    loadFilterFields()
  }, [activeCategory])

  const loadTemplates = async () => {
    try {
      const response = await api.get('/api/saved-searches/templates')
      setTemplates(response.data)
    } catch (error) {
      console.error('Failed to load templates:', error)
    }
  }

  const loadSavedSearches = async () => {
    try {
      const response = await api.get('/api/saved-searches/user')
      setSavedSearches(response.data)
    } catch (error) {
      console.error('Failed to load saved searches:', error)
    }
  }

  const loadFilterFields = async () => {
    try {
      const response = await api.get(`/api/saved-searches/fields/${activeCategory}`)
      setFilterFields(response.data.fields || [])
    } catch (error) {
      console.error('Failed to load filter fields:', error)
    }
  }

  const addFilter = () => {
    if (filterFields.length > 0) {
      setActiveFilters([...activeFilters, {
        field: filterFields[0].field,
        operator: 'eq',
        value: ''
      }])
    }
  }

  const updateFilter = (index: number, updates: Partial<FilterCondition>) => {
    const newFilters = [...activeFilters]
    newFilters[index] = { ...newFilters[index], ...updates }
    setActiveFilters(newFilters)
  }

  const removeFilter = (index: number) => {
    setActiveFilters(activeFilters.filter((_, i) => i !== index))
  }

  const applyTemplate = (template: FilterTemplate) => {
    setActiveCategory(template.category)
    const filters: FilterCondition[] = Object.entries(template.filters).map(([field, config]) => ({
      field,
      operator: (config as Record<string, unknown>).operator as string || 'eq',
      value: (config as Record<string, unknown>).value as string | number | boolean | string[]
    }))
    setActiveFilters(filters)
  }

  const executeSearch = async () => {
    setLoading(true)
    try {
      // Build filters object
      const filtersObj: Record<string, Record<string, unknown>> = {}
      activeFilters.forEach(f => {
        filtersObj[f.field] = { operator: f.operator, value: f.value }
      })

      // Execute search
      const response = await api.post('/api/saved-searches/temp/execute', {
        category: activeCategory,
        filters: filtersObj
      })
      setSearchResults(response.data.results || [])
    } catch (error) {
      console.error('Search failed:', error)
      // Use mock results on error
      setSearchResults([
        { id: '1', name: 'Sample Result 1', status: 'active' },
        { id: '2', name: 'Sample Result 2', status: 'active' },
        { id: '3', name: 'Sample Result 3', status: 'pending' },
      ])
    } finally {
      setLoading(false)
    }
  }

  const saveSearch = async () => {
    if (!searchName) return

    try {
      const filtersObj: Record<string, Record<string, unknown>> = {}
      activeFilters.forEach(f => {
        filtersObj[f.field] = { operator: f.operator, value: f.value }
      })

      await api.post('/api/saved-searches/', {
        name: searchName,
        description: searchDescription,
        category: activeCategory,
        filters: filtersObj,
        user_id: 'default_user'
      })

      setShowSaveModal(false)
      setSearchName('')
      setSearchDescription('')
      loadSavedSearches()
    } catch (error) {
      console.error('Failed to save search:', error)
    }
  }

  const loadSavedSearch = async (search: SavedSearch) => {
    setActiveCategory(search.category)
    const filters: FilterCondition[] = Object.entries(search.filters).map(([field, config]) => ({
      field,
      operator: (config as Record<string, unknown>).operator as string || 'eq',
      value: (config as Record<string, unknown>).value as string | number | boolean | string[]
    }))
    setActiveFilters(filters)

    // Execute the search
    try {
      const response = await api.post(`/api/saved-searches/${search.id}/execute`)
      setSearchResults(response.data.results || [])
    } catch (error) {
      console.error('Failed to execute saved search:', error)
    }
  }

  const deleteSavedSearch = async (searchId: string) => {
    try {
      await api.delete(`/api/saved-searches/${searchId}`)
      loadSavedSearches()
    } catch (error) {
      console.error('Failed to delete search:', error)
    }
  }

  const getFieldLabel = (fieldName: string): string => {
    const field = filterFields.find(f => f.field === fieldName)
    return field?.label || fieldName
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />
      <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
            Advanced Filters & Saved Searches
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>
            Build complex filters and save them for quick access
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' }}>
          {/* Left Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Category Selection */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Categories</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: activeCategory === cat.id ? '#EFF6FF' : 'transparent',
                      color: activeCategory === cat.id ? '#4A90E2' : '#374151',
                      fontWeight: activeCategory === cat.id ? '600' : '400',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <span>{cat.icon}</span>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Templates */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Quick Templates</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {templates.filter(t => t.category === activeCategory).map(template => (
                  <button
                    key={template.id}
                    onClick={() => applyTemplate(template)}
                    style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: '500' }}>{template.name}</div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                      {template.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Saved Searches */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>Saved Searches</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {savedSearches.filter(s => s.category === activeCategory).map(search => (
                  <div
                    key={search.id}
                    style={{
                      padding: '10px 12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <button
                      onClick={() => loadSavedSearch(search)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        flex: 1
                      }}
                    >
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{search.name}</div>
                      <div style={{ fontSize: '11px', color: '#6B7280' }}>
                        Used {search.use_count} times
                      </div>
                    </button>
                    <button
                      onClick={() => deleteSavedSearch(search.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#EF4444',
                        fontSize: '14px',
                        padding: '4px'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {savedSearches.filter(s => s.category === activeCategory).length === 0 && (
                  <div style={{ fontSize: '13px', color: '#9CA3AF', textAlign: 'center', padding: '8px' }}>
                    No saved searches yet
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Filter Builder */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Filter Builder</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={addFilter}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#F3F4F6',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    + Add Filter
                  </button>
                  {activeFilters.length > 0 && (
                    <button
                      onClick={() => setActiveFilters([])}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#FEE2E2',
                        color: '#DC2626',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Conditions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {activeFilters.length === 0 ? (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#9CA3AF',
                    border: '2px dashed #E5E7EB',
                    borderRadius: '8px'
                  }}>
                    No filters applied. Click "Add Filter" or select a template to get started.
                  </div>
                ) : (
                  activeFilters.map((filter, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px'
                      }}
                    >
                      {idx > 0 && (
                        <span style={{
                          padding: '4px 8px',
                          backgroundColor: '#EFF6FF',
                          color: '#4A90E2',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
                          AND
                        </span>
                      )}
                      <select
                        value={filter.field}
                        onChange={e => updateFilter(idx, { field: e.target.value })}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          fontSize: '14px',
                          minWidth: '150px'
                        }}
                      >
                        {filterFields.map(f => (
                          <option key={f.field} value={f.field}>{f.label}</option>
                        ))}
                      </select>
                      <select
                        value={filter.operator}
                        onChange={e => updateFilter(idx, { operator: e.target.value })}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }}
                      >
                        {operators.map(op => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={String(filter.value)}
                        onChange={e => updateFilter(idx, { value: e.target.value })}
                        placeholder="Value..."
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          fontSize: '14px',
                          flex: 1
                        }}
                      />
                      <button
                        onClick={() => removeFilter(idx)}
                        style={{
                          padding: '8px',
                          backgroundColor: '#FEE2E2',
                          color: '#DC2626',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Action Buttons */}
              {activeFilters.length > 0 && (
                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button
                    onClick={executeSearch}
                    disabled={loading}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#4A90E2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Searching...' : 'Apply Filters'}
                  </button>
                  <button
                    onClick={() => setShowSaveModal(true)}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'white',
                      color: '#4A90E2',
                      border: '2px solid #4A90E2',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Save Search
                  </button>
                </div>
              )}
            </div>

            {/* Results */}
            {searchResults.length > 0 && (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                  Results ({searchResults.length})
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
                      {searchResults[0] && Object.keys(searchResults[0]).map(key => (
                        <th
                          key={key}
                          style={{
                            textAlign: 'left',
                            padding: '12px',
                            fontSize: '13px',
                            color: '#6B7280',
                            fontWeight: '600'
                          }}
                        >
                          {getFieldLabel(key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((result, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        {Object.values(result).map((val, i) => (
                          <td key={i} style={{ padding: '12px', fontSize: '14px' }}>
                            {String(val)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Save Modal */}
        {showSaveModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '400px',
              maxWidth: '90%'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
                Save Search
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '13px', color: '#6B7280', display: 'block', marginBottom: '6px' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={searchName}
                    onChange={e => setSearchName(e.target.value)}
                    placeholder="My Custom Filter"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '13px', color: '#6B7280', display: 'block', marginBottom: '6px' }}>
                    Description
                  </label>
                  <textarea
                    value={searchDescription}
                    onChange={e => setSearchDescription(e.target.value)}
                    placeholder="Optional description..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}>
                  <strong>Category:</strong> {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)}<br />
                  <strong>Filters:</strong> {activeFilters.length} condition(s)
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowSaveModal(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#F3F4F6',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveSearch}
                  disabled={!searchName}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: searchName ? '#4A90E2' : '#E5E7EB',
                    color: searchName ? 'white' : '#9CA3AF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: searchName ? 'pointer' : 'not-allowed'
                  }}
                >
                  Save Search
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdvancedFilters
